import React, { useMemo } from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { DASHBOARD } from "../dashboard/dashboardTheme";
import {
  getReportingYearOptions,
  getSelectableQuarters,
  type NetworkReportingPeriod,
} from "@utils/networkReportingPeriod";

export interface NetworkReportingPeriodSelectorProps {
  period: NetworkReportingPeriod;
  onChange: (period: NetworkReportingPeriod) => void;
}

const selectSx = {
  fontSize: "13px",
  fontWeight: 700,
  color: DASHBOARD.header,
  height: 28,
  minWidth: 0,
  "& .MuiSelect-select": {
    py: 0.25,
    px: 0.75,
    pr: "24px !important",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${DASHBOARD.border}`,
  },
  bgcolor: "transparent",
};

export default function NetworkReportingPeriodSelector({
  period,
  onChange,
}: NetworkReportingPeriodSelectorProps) {
  const yearOptions = useMemo(() => getReportingYearOptions(), []);
  const quarterOptions = useMemo(
    () => getSelectableQuarters(period.year),
    [period.year],
  );

  const handleQuarterChange = (event: SelectChangeEvent<number>) => {
    onChange({ ...period, quarter: Number(event.target.value) });
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const year = Number(event.target.value);
    const quarters = getSelectableQuarters(year);
    const quarter = quarters.includes(period.quarter)
      ? period.quarter
      : quarters[quarters.length - 1];
    onChange({ quarter, year });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Select
          value={period.quarter}
          onChange={handleQuarterChange}
          size="small"
          variant="outlined"
          aria-label="Reporting quarter"
          sx={{ ...selectSx, width: 56 }}
        >
          {quarterOptions.map((q) => (
            <MenuItem key={q} value={q} sx={{ fontSize: "13px" }}>
              Q{q}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={period.year}
          onChange={handleYearChange}
          size="small"
          variant="outlined"
          aria-label="Reporting year"
          sx={{ ...selectSx, width: 72 }}
        >
          {yearOptions.map((y) => (
            <MenuItem key={y} value={y} sx={{ fontSize: "13px" }}>
              {y}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted, mt: 0.25 }}>
        vs prior quarter
      </Typography>
    </Box>
  );
}
